import { useState } from 'react';
import { Btn } from '../../components/ui/Btn.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { today } from '../../lib/utils';
import { C } from '../../theme/palette';

/**
 * Historique daté des échanges avec un client, comme dans la maquette V288 :
 * chaque appel, e-mail ou rendez-vous s'ajoute avec sa date, le plus récent
 * en tête, et chaque entrée se supprime.
 *
 * Il remplace la note de suivi unique, que chaque appel écrasait. Cette note
 * a été reprise par le serveur comme premier échange ; si elle n'y figure pas
 * (note écrite depuis un appareil resté sur l'ancienne version), elle reste
 * affichée en lecture, « 📌 Ancienne note », pour ne jamais se perdre.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.client client affiché
 * @param {Function} props.setClients mise à jour de la collection des clients
 * @returns {import('react').ReactElement} l'historique et sa saisie
 */
export function HistoriqueEchanges({ client, setClients }) {
  const [saisie, setSaisie] = useState('');
  const histo = client.histo || [];
  const modifie = (calcul) =>
    setClients((p) =>
      p.map((c) => (c.id === client.id ? { ...c, histo: calcul(c.histo || []) } : c)),
    );
  const ajoute = () => {
    const texte = saisie.trim();
    if (!texte) return;
    modifie((h) => [...h, { date: today(), texte }]);
    setSaisie('');
  };
  const ancienneNote =
    client.suivi &&
    client.suivi.trim() &&
    !histo.some((h) => String(h.texte ?? '').trim() === client.suivi.trim())
      ? client.suivi
      : '';

  return (
    <>
      <div
        style={{
          fontSize: 8.5,
          fontWeight: 800,
          color: C.muted,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          margin: '4px 0 6px',
        }}
      >
        Historique des échanges
      </div>
      {histo.length > 0 ? (
        <ul
          aria-label="Historique des échanges"
          style={{ listStyle: 'none', margin: '0 0 9px', padding: 0 }}
        >
          {histo
            .map((h, rang) => ({ ...h, rang }))
            .reverse()
            .map((h) => (
              <li
                key={h.rang}
                style={{
                  borderLeft: '2px solid ' + C.amber,
                  paddingLeft: 9,
                  paddingRight: 30,
                  marginBottom: 8,
                  position: 'relative',
                }}
              >
                <div style={{ fontSize: 8, color: C.muted, fontWeight: 700 }}>{h.date}</div>
                <div style={{ fontSize: 10, color: C.ink, whiteSpace: 'pre-wrap' }}>{h.texte}</div>
                <button
                  type="button"
                  aria-label={'Supprimer l’échange du ' + h.date}
                  onClick={() => modifie((liste) => liste.filter((_, k) => k !== h.rang))}
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 28,
                    height: 28,
                    border: 'none',
                    background: 'none',
                    fontSize: 9,
                    color: C.muted,
                    cursor: 'pointer',
                  }}
                >
                  ✕
                </button>
              </li>
            ))}
        </ul>
      ) : (
        <div style={{ fontSize: 9, color: C.muted, marginBottom: 9, fontStyle: 'italic' }}>
          Aucun échange noté pour l’instant.
        </div>
      )}
      {ancienneNote && (
        <div
          style={{
            fontSize: 9,
            color: C.grey,
            marginBottom: 9,
            background: C.navyXL,
            borderRadius: 8,
            padding: '7px 9px',
            whiteSpace: 'pre-wrap',
          }}
        >
          📌 Ancienne note : {ancienneNote}
        </div>
      )}
      <Inp
        label="Nouvel échange (appel, e-mail, RDV…)"
        rows={2}
        value={saisie}
        onChange={(e) => setSaisie(e.target.value)}
        placeholder="Ex : Appelé pour relance — rappeler lundi"
      />
      <Btn label="＋ Ajouter au suivi" ghost color={C.amber} full onClick={ajoute} />
    </>
  );
}
